// src/components/Layout.js
import React, { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { 
  Menu, X, Calculator, ArrowDown, 
  Phone, Mail, MapPin, Instagram, Facebook, Sun, ChevronRight, Zap, Home, Cog, Lightbulb, Trees
} from 'lucide-react';

import logoTopLux from '../assets/logo.png'; 

// Cores Globais
const colors = {
  navy: '#0A2540',
  darkNavy: '#061829',
  yellow: '#8ad7f7',
  gold: '#8ad7f7',
  white: '#FFFFFF',
  lightGray: '#F8F9FA',
  gray: '#E9ECEF',
  darkGray: '#343A40',
  accent: '#8ad7f7',
};

// --- COMPONENTES AUXILIARES ---
const servicesData = [
  {
    benefits: [
      "Economia de até 95%",
      "Valorização do imóvel",
      "Energia limpa",
    ],
  },
];

export const AnimatedBackgroundPoints = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    <div className="absolute inset-0 opacity-5">
      <div className="absolute inset-0" style={{
        backgroundImage: `radial-gradient(circle at 2px 2px, ${colors.yellow} 1px, transparent 0)`,
        backgroundSize: '60px 60px'
      }} />
    </div>
    {[...Array(20)].map((_, i) => (
      <motion.div
        key={i}
        className="absolute w-1 h-1 rounded-full"
        style={{
          backgroundColor: colors.yellow,
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
        }}
        animate={{
          y: [0, -40, 0],
          opacity: [0, 0.8, 0],
        }}
        transition={{
          duration: 4 + Math.random() * 3,
          repeat: Infinity,
          delay: Math.random() * 2,
        }}
      />
    ))}
  </div>
);

export const BudgetButton = ({ text, msg }) => (
  <motion.button
    onClick={() => window.open(`https://wa.me/5561983479438?text=${encodeURIComponent(msg || "Olá! Gostaria de solicitar um orçamento.")}`, '_blank')}
    className="relative px-4 sm:px-6 py-2.5 sm:py-3 rounded-full font-bold text-xs sm:text-base flex items-center justify-center gap-1.5 sm:gap-2 shadow-xl overflow-hidden group whitespace-nowrap"
    style={{ backgroundColor: colors.yellow, color: colors.navy }}
    whileHover={{ scale: 1.05 }}
    whileTap={{ scale: 0.95 }}
  >
    <Calculator className="w-3.5 h-3.5 sm:w-4 sm:h-4 relative z-10 flex-shrink-0" />
    <span className="relative z-10">{text || "Orçamento"}</span>
    <motion.div
      className="absolute inset-0 opacity-30 bg-white"
      animate={{ x: ['-100%', '100%'] }}
      transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 3 }}
    />
  </motion.button>
);

// --- HEADER ---
export const Header = ({ currentPage, setCurrentPage }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { id: 'home', label: 'Início' },
    { id: 'about', label: 'Sobre' },
    { id: 'services', label: 'Serviços' },
    { id: 'gallery', label: 'Projetos' },
    { id: 'reviews', label: 'Avaliações' },
    { id: 'maintenance', label: 'Manutenção' }
  ];

  const handleNavClick = (id) => {
    setCurrentPage(id);
    setMenuOpen(false);
    if (id === 'home') window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <motion.header
        className="fixed top-0 left-0 right-0 z-[999] transition-all duration-300"
        style={{
          backgroundColor: scrolled ? colors.darkNavy : 'transparent',
          backdropFilter: scrolled ? 'blur(10px)' : 'none',
          padding: scrolled ? '10px 0' : '16px 0',
          boxShadow: scrolled ? '0 4px 20px rgba(0,0,0,0.3)' : 'none'
        }}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
      >
        {!scrolled && <div className="absolute inset-0 bg-gradient-to-b from-black/70 to-transparent -z-10" />}

        <div className="max-w-7xl mx-auto px-3 sm:px-6 flex items-center justify-between">
          <motion.div 
            className="flex items-center gap-2 sm:gap-3 cursor-pointer flex-shrink-0"
            onClick={() => handleNavClick('home')}
            whileHover={{ scale: 1.05 }}
          >
            <img src={logoTopLux} alt="Top Lux" className="w-8 h-8 sm:w-12 sm:h-12 rounded-full border-2 border-[#8ad7f7] shadow-lg" />
            <div className="flex flex-col">
              <h1 className="text-base sm:text-xl font-black tracking-tight text-white leading-none">TOP LUX</h1>
              <p className="text-[8px] sm:text-[10px] font-bold tracking-widest text-[#8ad7f7] uppercase">Energia Solar</p>
            </div>
          </motion.div>

          <nav className="hidden lg:flex items-center gap-1.5">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleNavClick(item.id)}
                className={`px-3.5 py-2 text-sm rounded-full font-semibold transition-all ${
                  currentPage === item.id 
                    ? 'bg-[#8ad7f7] text-slate-900 shadow-lg shadow-[#8ad7f7]/20' 
                    : 'text-white hover:bg-white/10'
                }`}
              >
                {item.label}
              </button>
            ))}
            <div className="ml-3"><BudgetButton text="Orçamento" /></div>
          </nav>

          <button onClick={() => setMenuOpen(!menuOpen)} className="lg:hidden text-[#8ad7f7] p-2 flex-shrink-0">
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {menuOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} 
              className="fixed inset-0 bg-black/80 z-[1000]" onClick={() => setMenuOpen(false)} />
            <motion.div 
              initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
              className="fixed inset-y-0 right-0 w-[85%] max-w-sm bg-[#0A2540] z-[1001] p-5 sm:p-6 shadow-2xl overflow-y-auto"
            >
              <div className="flex justify-end mb-6 sm:mb-8"><X size={28} className="text-white cursor-pointer" onClick={() => setMenuOpen(false)} /></div>
              <div className="flex flex-col gap-3">
                {navItems.map((item) => (
                  <button key={item.id} onClick={() => handleNavClick(item.id)}
                    className={`text-left text-lg sm:text-xl font-bold py-2.5 border-b border-white/10 ${currentPage === item.id ? 'text-[#8ad7f7]' : 'text-white'}`}>
                    {item.label}
                  </button>
                ))}
              </div>
              <div className="mt-6 sm:mt-8"><BudgetButton text="Solicitar no WhatsApp" /></div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

// --- HERO PREMIUM MELHORADO ---
export const PremiumHero = ({ setCurrentPage }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 150]);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  const benefitIcons = [
    { icon: Zap, label: "Economia" },
    { icon: Home, label: "Imóvel" },
    { icon: Cog, label: "Projeto" }
  ];

  return (
    <section id="home" ref={ref} className="relative min-h-screen flex items-center justify-center overflow-hidden pt-24 pb-12 px-4 sm:px-6" style={{ backgroundColor: colors.darkNavy }}>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(10,37,64,0.8),#061829)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_80%,rgba(138,215,247,0.1),transparent_50%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(138,215,247,0.05),transparent_50%)]" />
      <AnimatedBackgroundPoints />
      
      <motion.div style={{ y, opacity }} className="relative z-10 text-center w-full max-w-5xl mx-auto">
        <motion.div initial={{ scale: 0, rotate: -90 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: 'spring', stiffness: 50, duration: 1.5 }} className="mb-6 sm:mb-8 inline-block relative">
          <div className="absolute -inset-4 bg-gradient-to-r from-[#8ad7f7]/20 via-transparent to-[#8ad7f7]/20 blur-2xl rounded-full" />
          <h1 className="relative text-2xl sm:text-4xl lg:text-6xl font-black text-white leading-tight px-2">
            Energia Solar <br/>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#8ad7f7] via-[#5ec1f0] to-[#8ad7f7]">Premium</span>
          </h1>
          <p className="text-xs sm:text-sm lg:text-base text-gray-300 mt-3 sm:mt-4 font-light px-2">Transforme seu consumo em economia sustentável</p>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 sm:gap-3 my-6 sm:my-8 mb-8 w-full max-w-4xl mx-auto"
        >
          {servicesData[0].benefits.map((benefit, id) => {
            const IconComponent = benefitIcons[id]?.icon || Zap;
            return (
              <motion.div
                key={id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + id * 0.1 }}
                whileHover={{ 
                  y: -4, 
                  backgroundColor: 'rgba(138, 215, 247, 0.1)',
                  borderColor: 'rgba(138, 215, 247, 0.5)'
                }}
                className="group relative p-3 sm:p-4 rounded-lg sm:rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm transition-all duration-300 cursor-pointer overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-[#8ad7f7]/0 to-[#8ad7f7]/0 group-hover:from-[#8ad7f7]/10 group-hover:to-[#8ad7f7]/10 transition-all duration-300" />
                
                <div className="relative z-10">
                  <motion.div 
                    whileHover={{ rotate: 20, scale: 1.1 }}
                    className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-gradient-to-br from-[#8ad7f7] to-[#5ec1f0] flex items-center justify-center mx-auto mb-2 sm:mb-3 shadow-lg shadow-[#8ad7f7]/30"
                  >
                    <IconComponent className="w-4 h-4 sm:w-5 sm:h-5 text-navy" />
                  </motion.div>
                  
                  <p className="text-xs sm:text-sm font-semibold text-white leading-relaxed group-hover:text-[#8ad7f7] transition-colors text-center">
                    {benefit}
                  </p>
                </div>

                <div className="absolute inset-0 rounded-lg sm:rounded-xl border border-[#8ad7f7]/0 group-hover:border-[#8ad7f7]/50 transition-all duration-300" />
              </motion.div>
            );
          })}
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="mb-6 px-2"
        >
          <p className="text-base sm:text-lg lg:text-xl text-gray-200 mb-4 sm:mb-6 font-light leading-relaxed">
            Economize até <span className="text-[#8ad7f7] font-black text-xl sm:text-2xl lg:text-3xl">95% </span> 
            <br className="hidden sm:block" />
            na sua conta de luz
          </p>
          
          <motion.button
            whileHover={{ scale: 1.08, boxShadow: '0 0 40px rgba(138, 215, 247, 0.4)' }}
            whileTap={{ scale: 0.92 }}
            onClick={() => window.open(`https://wa.me/5561983479438?text=${encodeURIComponent("Olá! Gostaria de solicitar um orçamento.")}`, '_blank')}
            className="relative px-5 sm:px-10 py-2.5 sm:py-3 rounded-full font-bold text-xs sm:text-base flex items-center justify-center gap-1.5 sm:gap-2 shadow-2xl overflow-hidden group mx-auto whitespace-nowrap"
            style={{ backgroundColor: colors.yellow, color: colors.navy }}
          >
            <Zap className="w-3.5 h-3.5 sm:w-4 sm:h-4 group-hover:animate-spin flex-shrink-0" />
            <span>Quero Economizar</span>
            <motion.div
              className="absolute inset-0 opacity-20 bg-white"
              animate={{ x: ['-100%', '100%'] }}
              transition={{ duration: 2, repeat: Infinity, repeatDelay: 2 }}
            />
          </motion.button>
        </motion.div>


      </motion.div>
    </section>
  );
};

// --- FOOTER ---
export const Footer = ({ setCurrentPage }) => {
  const handleNavClick = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="py-12 sm:py-16 bg-[#0A2540] text-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-5 bg-[radial-gradient(circle_at_bottom_left,_var(--tw-gradient-stops))] from-[#8ad7f7] to-transparent" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <img src={logoTopLux} alt="Logo" className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border border-[#8ad7f7] flex-shrink-0" />
            <div className="min-w-0">
              <h3 className="font-black text-lg sm:text-xl">TOP LUX</h3>
              <p className="text-xs text-[#8ad7f7]">Energia Solar</p>
            </div>
          </div>
          <p className="text-gray-400 text-xs sm:text-sm leading-relaxed mb-4">Transformando sol em economia e sustentabilidade para o seu futuro.</p>
          <div className="flex gap-2 sm:gap-3">
            <a 
              href="https://www.instagram.com/topluxenergia/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="p-2 bg-white/10 rounded-full hover:bg-[#8ad7f7] hover:text-black transition"
            >
              <Instagram size={18} />
            </a>
            <a 
              href="https://www.facebook.com/people/Top-Lux-Energia-Solar/61572632664762/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="p-2 bg-white/10 rounded-full hover:bg-[#8ad7f7] hover:text-black transition"
            >
              <Facebook size={18} />
            </a>
          </div>
        </div>

        <div>
          <h4 className="font-bold text-[#8ad7f7] mb-4 flex items-center gap-2 text-sm sm:text-base">
            <ChevronRight size={16}/> Navegação
          </h4>
          <ul className="space-y-2 text-gray-300 text-xs sm:text-sm">
            <li>
              <button 
                onClick={() => handleNavClick('home')} 
                className="hover:text-[#8ad7f7] hover:translate-x-1 transition-all block"
              >
                Início
              </button>
            </li>
            <li>
              <button 
                onClick={() => handleNavClick('about')} 
                className="hover:text-[#8ad7f7] hover:translate-x-1 transition-all block"
              >
                Sobre
              </button>
            </li>
            <li>
              <button 
                onClick={() => handleNavClick('services')} 
                className="hover:text-[#8ad7f7] hover:translate-x-1 transition-all block"
              >
                Serviços
              </button>
            </li>
            <li>
              <button 
                onClick={() => handleNavClick('gallery')} 
                className="hover:text-[#8ad7f7] hover:translate-x-1 transition-all block"
              >
                Projetos
              </button>
            </li>
          </ul>
        </div>

        <div>
          <h4 className="font-bold text-[#8ad7f7] mb-4 flex items-center gap-2 text-sm sm:text-base"><Phone size={16}/> Contato</h4>
          <ul className="space-y-2.5 sm:space-y-3 text-gray-300 text-xs sm:text-sm">
            <li className="flex gap-2 sm:gap-3 items-start sm:items-center">
              <a href="https://wa.me/5561983479438" target="_blank" rel="noopener noreferrer" className="flex gap-2 sm:gap-3 hover:text-[#8ad7f7] transition-colors items-center min-w-0">
                <Phone size={16} className="text-[#8ad7f7] shrink-0"/> <span className="break-all">(61) 98347-9438</span>
              </a>
            </li>
            <li className="flex gap-2 sm:gap-3 items-start sm:items-center">
              <a href="mailto:topluxsolar@gmail.com" className="flex gap-2 sm:gap-3 hover:text-[#8ad7f7] transition-colors items-center min-w-0">
                <Mail size={16} className="text-[#8ad7f7] shrink-0"/> <span className="break-all">topluxsolar@gmail.com</span>
              </a>
            </li>
            <li className="flex gap-2 sm:gap-3 items-start sm:items-center">
              <a href="https://www.google.com/maps/search/Brasília,+DF" target="_blank" rel="noopener noreferrer" className="flex gap-2 sm:gap-3 hover:text-[#8ad7f7] transition-colors items-center min-w-0">
                <MapPin size={16} className="text-[#8ad7f7] shrink-0"/> <span>Brasília - DF</span>
              </a>
            </li>
            <li className="flex gap-2 sm:gap-3 items-start sm:items-center">
              <a href="https://www.google.com/maps/search/Formosa,+GO" target="_blank" rel="noopener noreferrer" className="flex gap-2 sm:gap-3 hover:text-[#8ad7f7] transition-colors items-center min-w-0">
                <MapPin size={16} className="text-[#8ad7f7] shrink-0"/> <span>Formosa - GO</span>
              </a>
            </li>
          </ul>
        </div>

        <div>
          <h4 className="font-bold text-[#8ad7f7] mb-4 flex items-center gap-2 text-sm sm:text-base"><Sun size={16}/> Atendimento</h4>
          <div className="bg-white/5 p-3 sm:p-4 rounded-lg sm:rounded-xl border border-white/10">
            <p className="text-xs sm:text-sm font-bold">Segunda a Sexta</p>
            <p className="text-xs text-gray-400 mb-2">08:00 - 18:00</p>
            <p className="text-xs sm:text-sm font-bold">Sábado</p>
            <p className="text-xs text-gray-400">08:00 - 12:00</p>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 mt-10 sm:mt-12 pt-6 sm:pt-8 border-t border-white/10 text-center text-xs text-gray-500 relative z-20">
          <p className="inline">
            &copy; {new Date().getFullYear()} Top Lux Energia Solar. Desenvolvido por{" "}
            <a 
              href="https://github.com/Joaozzin-dev" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-white hover:text-[#8ad7f7] transition-colors underline cursor-pointer relative z-30"
              style={{ pointerEvents: 'auto' }}
            >
              Joaozzin-dev
            </a>
            .
          </p>
      </div>
    </footer>
  );
};

// --- BOTÃO FLUTUANTE ---
export const FloatingWhatsAppButton = () => (
  <motion.a
    href="https://wa.me/5561983479438"
    target="_blank"
    rel="noopener noreferrer"
    className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-green-500 rounded-full flex items-center justify-center shadow-lg text-white hover:bg-green-600 transition-colors"
    whileHover={{ scale: 1.1 }}
    whileTap={{ scale: 0.9 }}
    initial={{ scale: 0 }}
    animate={{ scale: 1 }}
    transition={{ delay: 1 }}
  >
    <Phone size={28} fill="currentColor" />
  </motion.a>
);